import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import tw from 'twin.macro';

import { Spacer } from '@components/Spacer';
import { Button } from '@components/Button';
import { Input } from '@auth_module/components/Input';

import { fetchUserInfo, login } from '@auth_module/services';
import { EyeIcon, EyeIconCrossed } from '@app/components/icons/EyeIcon';

const StyledForm = tw.form`flex flex-col w-full gap-[12px]`;
const ClickableIconWrapper = tw.div`cursor-pointer`;

type LoginFormData = {
  email: string;
  password: string;
};

type LoginFormProps = {
  handleSuccessLogin: (token: string, castedAdmin?: string) => void;
  cid?: string;
};

const LoginForm = ({ handleSuccessLogin, cid }: LoginFormProps) => {
  const { t } = useTranslation();

  const loginSchema = useMemo(() => {
    return yup.object().shape({
      email: yup
        .string()
        .required(t('auth.login_form.email.error_message_required') as string)
        .email(t('auth.login_form.email.error_message_invalid') as string),
      password: yup.string().required(t('auth.login_form.password.error_message') as string),
    });
  }, [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    setFocus,
  } = useForm<LoginFormData>({ resolver: yupResolver(loginSchema) });

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const handleEyeIconClick = () => {
    setIsPasswordVisible((prevState: boolean) => !prevState);
  };

  const onSubmit: SubmitHandler<LoginFormData> = (formData) => {
    login({ ...formData, cid })
      .then(({ data: loginData }) => {
        handleSuccessLogin(loginData.token, loginData.admin);
        fetchUserInfo();
      })
      .catch(() => setError('password', { message: t('auth.login_form.submit_error_message') }));
  };

  return (
    <>
      <h3 tw='text-accent-2 text-2xl'>{t('auth.login_form.title')}</h3>
      <Spacer direction='vertical' size='xl' />

      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Input
          autoFocus
          tw='w-full'
          type='text'
          label={t('auth.login_form.email.label')}
          {...register('email')}
          error={errors.email?.message}
        />

        <Input
          type={isPasswordVisible ? 'text' : 'password'}
          label={t('auth.login_form.password.label')}
          {...register('password')}
          error={errors.password?.message}
          renderIcon={() => (
            <ClickableIconWrapper
              onClick={() => {
                handleEyeIconClick();
                setFocus('password');
              }}>
              {isPasswordVisible ? (
                <EyeIconCrossed tw='fill-accent-2' />
              ) : (
                <EyeIcon tw='stroke-accent-2 fill-none' />
              )}
            </ClickableIconWrapper>
          )}
        />

        <Button.Primary
          type='submit'
          tw='bg-accent-2 text-text-and-icon-invert h-[40px] my-[12px]'
          disabled={isSubmitting}
          isLoading={isSubmitting}>
          {t('auth.login_form.submit_button')}
        </Button.Primary>
      </StyledForm>

      <Spacer direction='vertical' size='xl' />
      <a tw='text-xs text-accent-2' href='/forgot-password'>
        {t('auth.login_form.forgot_password')}
      </a>
    </>
  );
};

export { LoginForm };
